/**
 * Registration wizard.
 *
 * Flow (post-refactor — empresa primero, método después):
 *   persona:  choose-type → choose-method → person-form (si correo) → verify-otp → done
 *                                          ↘ OAuth provider → /oauth/callback → done
 *   empresa:  choose-type → nit-lookup → confirm-org → choose-method
 *                                                    → person-form (si correo) → verify-otp → done
 *                                                    ↘ OAuth provider → callback (org auto-creada)
 *
 * choose-method aloja: LegalAcceptanceCheckbox required + OAuthButtons
 * (deshabilitados si !accepted) + "Continuar con correo" (deshabilitado si !accepted).
 * Para empresa+OAuth, antes del redirect llamamos POST /auth/oauth/{p}/prepare
 * con los datos validados; el backend crea Organization + Membership tras el callback.
 */

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  Building2,
  Check,
  CheckCircle2,
  Loader2,
  Mail,
  RefreshCcw,
  Search,
  Shield,
  User,
  XCircle,
} from 'lucide-react'
import {
  authApi,
  lookupApi,
  setAccessToken,
  type RegisterStartResponse,
} from '../../lib/api'
import { ROUTES } from '../../lib/constants'
import { ThemeToggle } from '../../components/ui/theme-toggle'
import { OAuthDivider } from '../../components/ui/oauth-buttons'
import { LegalAcceptanceCheckbox } from '../../components/ui/legal-acceptance'
import { prepareOAuth, startOAuth, type OAuthProvider } from '../../lib/oauth'

// ─── State machine ───────────────────────────────────────────────────

type Step =
  | 'choose-type'
  | 'nit-lookup'
  | 'confirm-org'
  | 'choose-method'
  | 'person-form'
  | 'verify-otp'
  | 'done'

type AccountType = 'persona' | 'empresa'

interface OrgState {
  found: boolean | null   // null = not yet looked up, true = RUES hit, false = manual entry
  nit: string
  razon_social: string
  email_corporativo: string
  direccion: string
  departamento: string
  municipio: string
  rep_legal: string
  organizacion_juridica: string
  tamano_empresa: string
  actividades_economicas: string[]
}

const EMPTY_ORG: OrgState = {
  found: null,
  nit: '',
  razon_social: '',
  email_corporativo: '',
  direccion: '',
  departamento: '',
  municipio: '',
  rep_legal: '',
  organizacion_juridica: '',
  tamano_empresa: '',
  actividades_economicas: [],
}

interface PersonState {
  first_name: string
  last_name: string
  email: string
  password: string
}

const EMPTY_PERSON: PersonState = {
  first_name: '',
  last_name: '',
  email: '',
  password: '',
}

// Versions vigentes — deben coincidir con app/legal/versions.py del backend.
// Cuando publiquemos nueva versión, actualizamos ambos lugares en el mismo PR.
const LEGAL_TERMS_VERSION = '1.0'
const LEGAL_HABEAS_VERSION = '1.0'

const cn = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ')

// ─── Page ───────────────────────────────────────────────────────────

export function RegisterPage() {
  const navigate = useNavigate()
  const [step, setStep] = useState<Step>('choose-type')
  const [accountType, setAccountType] = useState<AccountType>('empresa')
  const [org, setOrg] = useState<OrgState>(EMPTY_ORG)
  const [person, setPerson] = useState<PersonState>(EMPTY_PERSON)
  const [acceptedLegal, setAcceptedLegal] = useState(false)
  const [start, setStart] = useState<RegisterStartResponse | null>(null)
  const [personalCode, setPersonalCode] = useState('')
  const [corporateCode, setCorporateCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attemptsRemaining, setAttemptsRemaining] = useState<number | null>(null)

  // Stepper config — different lengths per account type. choose-method
  // siempre va entre la validación (empresa o tipo) y los datos personales.
  const steps: { id: Step; label: string; icon: typeof User }[] =
    accountType === 'empresa'
      ? [
          { id: 'choose-type', label: 'Tipo', icon: Building2 },
          { id: 'nit-lookup', label: 'NIT', icon: Search },
          { id: 'confirm-org', label: 'Empresa', icon: Building2 },
          { id: 'choose-method', label: 'Método', icon: Shield },
          { id: 'person-form', label: 'Tú', icon: User },
          { id: 'verify-otp', label: 'Verificar', icon: Mail },
        ]
      : [
          { id: 'choose-type', label: 'Tipo', icon: User },
          { id: 'choose-method', label: 'Método', icon: Shield },
          { id: 'person-form', label: 'Tus datos', icon: User },
          { id: 'verify-otp', label: 'Verificar', icon: Mail },
        ]

  function next(s: Step) {
    setError(null)
    setStep(s)
  }

  // ─── Step actions ──────────────────────────────────────────────

  async function handleSubmitStart() {
    setError(null)
    setLoading(true)
    try {
      const body = {
        email: person.email.trim(),
        password: person.password,
        first_name: person.first_name.trim(),
        last_name: person.last_name.trim(),
        registration_type: accountType,
        // Habeas Data — autorización expresa (capturada en choose-method).
        accepted_terms_version: LEGAL_TERMS_VERSION,
        accepted_habeas_data_version: LEGAL_HABEAS_VERSION,
        accepted_marketing: false, // marketing se activa después desde Mi cuenta → Privacidad.
        ...(accountType === 'empresa' && {
          nit: org.nit.replace(/\D/g, ''),
          razon_social: org.razon_social.trim(),
          email_corporativo: org.email_corporativo.trim(),
          direccion: org.direccion.trim(),
          departamento: org.departamento.trim(),
          municipio: org.municipio.trim(),
          rues_verified: org.found === true, // backend re-verifies via empresas API
        }),
      }
      const resp = await authApi.startRegistration(
        body as Parameters<typeof authApi.startRegistration>[0],
      )
      setStart(resp)
      next('verify-otp')
    } catch (err) {
      setError(extractError(err))
    } finally {
      setLoading(false)
    }
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    if (!start) return
    setError(null)
    setLoading(true)
    try {
      const resp = await authApi.verifyRegistration({
        registration_token: start.registration_token,
        personal_code: personalCode.trim(),
        corporate_code: accountType === 'empresa' ? corporateCode.trim() : undefined,
      })
      setAccessToken(resp.access_token)
      next('done')
      setTimeout(() => navigate(ROUTES.DASHBOARD, { replace: true }), 1800)
    } catch (err) {
      const e = err as { response?: { status?: number; data?: { attempts_remaining?: number; message?: string } } }
      if (e.response?.status === 400 && e.response.data?.attempts_remaining !== undefined) {
        setAttemptsRemaining(e.response.data.attempts_remaining)
        setError(e.response.data.message ?? 'Código incorrecto.')
      } else {
        setError(extractError(err))
      }
    } finally {
      setLoading(false)
    }
  }

  async function handleResend(target: 'personal' | 'corporate') {
    if (!start) return
    setError(null)
    setLoading(true)
    try {
      await authApi.resendOTP(start.registration_token, target)
      setError(`Código reenviado al correo ${target === 'personal' ? 'personal' : 'corporativo'}.`)
    } catch (err) {
      setError(extractError(err))
    } finally {
      setLoading(false)
    }
  }

  // ─── Render ────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-bg-app">
      {/* Warm grid overlay (driven by --color-grid in tokens.css) */}
      <div aria-hidden className="grid-overlay-radial pointer-events-none fixed inset-0" />

      <div className="relative mx-auto grid min-h-screen max-w-7xl grid-cols-1 lg:grid-cols-12 lg:gap-8 lg:px-8">
        {/* ─── LEFT — wizard ─── */}
        <main className="px-5 pt-10 pb-16 sm:px-8 lg:col-span-7 lg:px-0 lg:pt-16">
          <div className="flex items-center justify-between">
            <a
              href={import.meta.env.VITE_APP_URL ?? 'https://taxor.co'}
              className="inline-flex items-center gap-1.5 font-mono text-xs text-fg-muted hover:text-fg-primary"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              volver al home
            </a>
            <ThemeToggle />
          </div>

          <h1 className="mt-6 text-3xl font-bold tracking-tight text-fg-primary sm:text-4xl">
            Crear cuenta en{' '}
            <span className="bg-gradient-to-br from-brand-700 to-brand-500 bg-clip-text text-transparent">
              Taxor
            </span>
          </h1>
          <p className="mt-3 max-w-md text-sm text-fg-secondary sm:text-base">
            Empresas y personas. Verificación por OTP. Datos de empresa
            tomados directamente del registro mercantil.
          </p>

          <div className="mt-8">
            <Stepper steps={steps} currentId={step} />
          </div>

          <div className="mt-6">
            {error && <Banner kind="error" message={error} attempts={attemptsRemaining ?? undefined} />}
          </div>

          <section className="mt-2">
            {step === 'choose-type' && (
              <ChooseType
                value={accountType}
                onChange={setAccountType}
                onNext={() => next(accountType === 'empresa' ? 'nit-lookup' : 'choose-method')}
              />
            )}

            {step === 'nit-lookup' && (
              <NitLookupStep
                org={org}
                setOrg={setOrg}
                onBack={() => next('choose-type')}
                onContinue={() => next('confirm-org')}
              />
            )}

            {step === 'confirm-org' && (
              <ConfirmOrgStep
                org={org}
                setOrg={setOrg}
                onBack={() => next('nit-lookup')}
                onContinue={() => next('choose-method')}
              />
            )}

            {step === 'choose-method' && (
              <ChooseMethodStep
                accountType={accountType}
                org={org}
                accepted={acceptedLegal}
                setAccepted={setAcceptedLegal}
                onBack={() => next(accountType === 'empresa' ? 'confirm-org' : 'choose-type')}
                onContinueWithEmail={() => next('person-form')}
                onError={(msg) => setError(msg)}
              />
            )}

            {step === 'person-form' && (
              <PersonStep
                accountType={accountType}
                person={person}
                setPerson={setPerson}
                org={org}
                onBack={() => next('choose-method')}
                onSubmit={handleSubmitStart}
                loading={loading}
              />
            )}

            {step === 'verify-otp' && start && (
              <VerifyOTPStep
                accountType={accountType}
                start={start}
                personalCode={personalCode}
                corporateCode={corporateCode}
                setPersonalCode={setPersonalCode}
                setCorporateCode={setCorporateCode}
                onSubmit={handleVerify}
                onResend={handleResend}
                loading={loading}
              />
            )}

            {step === 'done' && <DoneStep />}
          </section>
        </main>

        {/* ─── RIGHT — value panel ─── */}
        <aside className="hidden border-l border-border lg:col-span-5 lg:flex lg:flex-col lg:px-10 lg:pt-16 lg:pb-16">
          <ValuePanel />
        </aside>
      </div>
    </div>
  )
}

// ─── Stepper ─────────────────────────────────────────────────────────

function Stepper({
  steps,
  currentId,
}: {
  steps: { id: Step; label: string; icon: typeof User }[]
  currentId: Step
}) {
  const idx = steps.findIndex((s) => s.id === currentId)
  return (
    <div className="flex items-center gap-1.5">
      {steps.map((s, i) => {
        const Icon = s.icon
        const isActive = i === idx
        const isDone = i < idx || currentId === 'done'
        return (
          <div key={s.id} className="flex flex-1 items-center gap-1.5">
            <div
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full border transition-all duration-300',
                isDone
                  ? 'border-brand-500 bg-brand-500 text-white'
                  : isActive
                    ? 'border-brand-500 bg-brand-500/10 text-brand-700'
                    : 'border-border bg-bg-elevated text-fg-muted',
              )}
            >
              {isDone ? <Check className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
            </div>
            <span
              className={cn(
                'hidden text-xs font-medium transition-colors sm:inline',
                isActive
                  ? 'text-fg-primary'
                  : isDone
                    ? 'text-brand-700'
                    : 'text-fg-muted',
              )}
            >
              {s.label}
            </span>
            {i < steps.length - 1 && (
              <div
                className={cn(
                  'h-px flex-1 transition-colors duration-300',
                  i < idx ? 'bg-brand-500' : 'bg-border',
                )}
              />
            )}
          </div>
        )
      })}
    </div>
  )
}

// ─── Banner ──────────────────────────────────────────────────────────

function Banner({
  kind,
  message,
  attempts,
}: {
  kind: 'error' | 'info'
  message: string
  attempts?: number
}) {
  const Icon = kind === 'error' ? XCircle : CheckCircle2
  return (
    <div
      className={cn(
        'flex items-start gap-2 rounded-lg border px-3.5 py-2.5 text-sm',
        kind === 'error'
          ? 'border-warning-soft bg-warning-soft text-warning-fg'
          : 'border-success-soft bg-success-soft text-success-fg',
      )}
      style={{
        borderColor: kind === 'error' ? 'var(--color-warning-soft-bd)' : 'var(--color-success-soft-bd)',
      }}
    >
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="flex-1">
        {message}
        {attempts !== undefined && (
          <span className="ml-1 font-mono text-xs opacity-70">
            ({attempts} intento{attempts === 1 ? '' : 's'} restante{attempts === 1 ? '' : 's'})
          </span>
        )}
      </div>
    </div>
  )
}

// ─── Step: choose type ──────────────────────────────────────────────

function ChooseType({
  value,
  onChange,
  onNext,
}: {
  value: AccountType
  onChange: (v: AccountType) => void
  onNext: () => void
}) {
  return (
    <div className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold text-fg-primary">¿Qué tipo de cuenta vas a crear?</h2>
        <p className="mt-1 text-sm text-fg-secondary">
          Las dos opciones soportan facturación electrónica DIAN.
        </p>
      </header>

      <div className="grid gap-3 sm:grid-cols-2">
        <TypeCard
          selected={value === 'empresa'}
          onClick={() => onChange('empresa')}
          icon={Building2}
          title="Empresa"
          subtitle="Voy a facturar como persona jurídica."
          accent="brand"
          bullets={['Validamos tus datos oficiales', 'Autocompletado por NIT', 'Verificación dual por correo']}
        />
        <TypeCard
          selected={value === 'persona'}
          onClick={() => onChange('persona')}
          icon={User}
          title="Persona"
          subtitle="Solo soy un usuario. Crearé empresa después."
          accent="accent"
          bullets={['Setup en 30 segundos', '1 código de verificación', 'Crea organizaciones cuando quieras']}
        />
      </div>

      <PrimaryButton onClick={onNext} className="w-full sm:w-auto">
        {value === 'empresa' ? 'Validar empresa' : 'Continuar'}
        <ArrowRight className="h-4 w-4" />
      </PrimaryButton>

      <p className="text-xs text-fg-muted">
        {value === 'empresa'
          ? 'Siguiente paso: validar el NIT con el registro mercantil. Después escogerás Google, Microsoft o correo.'
          : 'Siguiente paso: escoger Google, Microsoft o correo.'}
      </p>
    </div>
  )
}

// ─── Step: choose method (post-validación) ──────────────────────────

function ChooseMethodStep({
  accountType,
  org,
  accepted,
  setAccepted,
  onBack,
  onContinueWithEmail,
  onError,
}: {
  accountType: AccountType
  org: OrgState
  accepted: boolean
  setAccepted: (v: boolean) => void
  onBack: () => void
  onContinueWithEmail: () => void
  onError: (msg: string) => void
}) {
  const [oauthBusy, setOauthBusy] = useState<OAuthProvider | null>(null)

  // Si es empresa, mandamos los datos pre-validados al backend antes
  // de redirigir al provider. El backend los guarda en el state Redis
  // y al completar el callback crea Organization + Membership.
  async function chooseOAuth(provider: OAuthProvider) {
    if (!accepted) return
    setOauthBusy(provider)
    try {
      if (accountType === 'empresa' && org.nit) {
        await prepareOAuth(provider, 'register', {
          nit: org.nit.replace(/\D/g, ''),
          razon_social: org.razon_social.trim(),
          email_corporativo: org.email_corporativo.trim() || null,
          direccion: org.direccion.trim() || null,
          organizacion_juridica: org.organizacion_juridica || null,
          tamano_empresa: org.tamano_empresa || null,
          rues_verified: org.found === true,
          country_iso: 'CO',
        })
      } else {
        startOAuth(provider, 'register')
      }
      // window.location.href ocurre dentro de las funciones; si volvemos
      // aquí es porque algo falló silenciosamente.
    } catch (err: unknown) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        'No pudimos iniciar el flujo OAuth.'
      onError(typeof detail === 'string' ? detail : 'No pudimos iniciar el flujo OAuth.')
      setOauthBusy(null)
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold text-fg-primary">¿Cómo quieres crear tu cuenta?</h2>
        <p className="mt-1 text-sm text-fg-secondary">
          {accountType === 'empresa'
            ? `Vas a registrar a ${org.razon_social || 'la empresa'} con el método que elijas.`
            : 'Crea tu cuenta personal con el método que prefieras.'}
        </p>
      </header>

      <LegalAcceptanceCheckbox
        accepted={accepted}
        setAccepted={setAccepted}
        actionVerb="crear mi cuenta"
      />

      <div className="rounded-xl border border-border bg-bg-elevated p-5 shadow-sm">
        <div className={cn('space-y-4', !accepted && 'opacity-60')}>
          <div className="space-y-2.5">
            <button
              type="button"
              onClick={() => chooseOAuth('google')}
              disabled={!accepted || oauthBusy !== null}
              className="flex w-full items-center justify-center gap-2.5 rounded-md border border-border bg-bg-app px-4 py-2.5 text-sm font-medium text-fg-primary transition-colors hover:border-fg-muted hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
            >
              {oauthBusy === 'google' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <GoogleSvg />
              )}
              Continuar con Google
            </button>
            <button
              type="button"
              onClick={() => chooseOAuth('microsoft')}
              disabled={!accepted || oauthBusy !== null}
              className="flex w-full items-center justify-center gap-2.5 rounded-md border border-border bg-bg-app px-4 py-2.5 text-sm font-medium text-fg-primary transition-colors hover:border-fg-muted hover:bg-bg-elevated disabled:cursor-not-allowed disabled:opacity-60"
            >
              {oauthBusy === 'microsoft' ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <MicrosoftSvg />
              )}
              Continuar con Microsoft
            </button>
          </div>

          <OAuthDivider label="o con tu correo" />

          <PrimaryButton
            onClick={onContinueWithEmail}
            disabled={!accepted || oauthBusy !== null}
            className="w-full justify-center"
          >
            Crear cuenta con correo
            <ArrowRight className="h-4 w-4" />
          </PrimaryButton>
        </div>
      </div>

      <NavRow
        onBack={onBack}
        onNext={() => {}}
        nextDisabled
        nextLabel="Elige un método arriba"
        nextLoading={false}
      />
    </div>
  )
}

// Inline brand SVGs (Google G + Microsoft 4-square) — duplicates the
// logic in components/ui/oauth-buttons.tsx so we can show the loading
// spinner per-provider without rewriting that shared component.
function GoogleSvg() {
  return (
    <svg viewBox="0 0 24 24" className="h-4 w-4" aria-hidden>
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.46 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  )
}

function MicrosoftSvg() {
  return (
    <svg viewBox="0 0 23 23" className="h-4 w-4" aria-hidden>
      <rect x="1" y="1" width="10" height="10" fill="#F25022" />
      <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
      <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  )
}

function TypeCard({
  selected,
  onClick,
  icon: Icon,
  title,
  subtitle,
  bullets,
  accent,
}: {
  selected: boolean
  onClick: () => void
  icon: typeof User
  title: string
  subtitle: string
  bullets: string[]
  accent: 'brand' | 'accent'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group relative overflow-hidden rounded-xl border-2 p-5 text-left transition-all duration-200',
        selected
          ? accent === 'brand'
            ? 'border-brand-500 bg-brand-500/5 shadow-md'
            : 'border-accent-500 bg-accent-500/5 shadow-md'
          : 'border-border bg-bg-elevated hover:border-fg-muted hover:shadow-sm',
      )}
    >
      <div
        className={cn(
          'absolute right-0 top-0 h-24 w-24 translate-x-8 -translate-y-8 rounded-full blur-2xl transition-opacity',
          selected ? 'opacity-60' : 'opacity-0 group-hover:opacity-30',
          accent === 'brand' ? 'bg-brand-500/30' : 'bg-accent-500/30',
        )}
      />
      <div className="relative">
        <div
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-lg',
            accent === 'brand' ? 'bg-brand-500/10 text-brand-700' : 'bg-accent-500/10 text-accent-600',
          )}
        >
          <Icon className="h-5 w-5" />
        </div>
        <h3 className="mt-3 text-base font-semibold text-fg-primary">{title}</h3>
        <p className="mt-1 text-sm text-fg-secondary">{subtitle}</p>
        <ul className="mt-3 space-y-1 text-xs text-fg-muted">
          {bullets.map((b) => (
            <li key={b} className="flex items-start gap-1.5">
              <Check className="mt-0.5 h-3 w-3 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </button>
  )
}

// ─── Step: NIT lookup ──────────────────────────────────────────────

function NitLookupStep({
  org,
  setOrg,
  onBack,
  onContinue,
}: {
  org: OrgState
  setOrg: React.Dispatch<React.SetStateAction<OrgState>>
  onBack: () => void
  onContinue: () => void
}) {
  const [input, setInput] = useState(org.nit)
  const [state, setState] = useState<'idle' | 'searching' | 'found' | 'not-found' | 'error'>(
    org.found === true ? 'found' : 'idle',
  )
  const [errMsg, setErrMsg] = useState<string | null>(null)

  async function lookup() {
    const clean = input.replace(/\D/g, '')
    if (clean.length < 9) {
      setErrMsg('El NIT debe tener al menos 9 dígitos.')
      setState('error')
      return
    }
    setErrMsg(null)
    setState('searching')
    try {
      const r = await lookupApi.byNIT(clean)
      if (r.found) {
        setOrg({
          found: true,
          nit: r.nit,
          razon_social: r.razon_social ?? '',
          email_corporativo: r.email_corporativo ?? '',
          direccion: r.direccion ?? '',
          departamento: r.departamento ?? '',
          municipio: r.municipio ?? '',
          rep_legal: r.rep_legal ?? '',
          organizacion_juridica: r.organizacion_juridica ?? '',
          tamano_empresa: r.tamano_empresa ?? '',
          actividades_economicas: r.actividades_economicas ?? [],
        })
        setState('found')
      } else {
        setOrg((prev) => ({ ...prev, found: false, nit: clean, razon_social: '', email_corporativo: '' }))
        setState('not-found')
      }
    } catch (err) {
      setErrMsg(extractError(err))
      setState('error')
    }
  }

  return (
    <div className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold text-fg-primary">¿Cuál es el NIT de la empresa?</h2>
        <p className="mt-1 text-sm text-fg-secondary">
          Buscamos los datos oficiales para autocompletar el formulario. Si no aparece,
          puedes ingresarlos manualmente.
        </p>
      </header>

      <div className="rounded-xl border border-border bg-bg-elevated p-5 shadow-sm">
        <Field label="NIT" hint="Solo dígitos. Sin DV ni puntos.">
          <div className="flex gap-2">
            <input
              autoFocus
              inputMode="numeric"
              value={input}
              onChange={(e) => setInput(e.target.value.replace(/[^\d.\-]/g, ''))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  lookup()
                }
              }}
              placeholder="901234567"
              className={inputStyles + ' font-mono'}
            />
            <PrimaryButton onClick={lookup} disabled={state === 'searching' || input.replace(/\D/g, '').length < 9}>
              {state === 'searching' ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Buscando
                </>
              ) : (
                <>
                  <Search className="h-4 w-4" />
                  Buscar
                </>
              )}
            </PrimaryButton>
          </div>
        </Field>

        {state === 'searching' && (
          <div className="mt-4 space-y-2">
            <div className="h-4 w-3/4 animate-pulse rounded bg-bg-app/60" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-bg-app/40" />
            <div className="h-3 w-2/3 animate-pulse rounded bg-bg-app/40" />
          </div>
        )}

        {state === 'found' && org.found === true && (
          <div className="mt-4 animate-in fade-in slide-in-from-bottom-1 duration-300">
            <RuesPreviewCard org={org} />
          </div>
        )}

        {state === 'not-found' && (
          <div className="mt-4 rounded-lg bg-warning-tint p-3 text-sm text-warning-fg">
            <div className="font-medium">No encontramos el NIT en los registros oficiales.</div>
            <div className="mt-1 text-xs opacity-80">
              Puedes continuar ingresando los datos manualmente. Quedarán pendientes de
              validación oficial.
            </div>
          </div>
        )}

        {state === 'error' && errMsg && (
          <div className="mt-4 rounded-lg bg-error-tint p-3 text-sm text-error-fg">
            {errMsg}
          </div>
        )}
      </div>

      <NavRow
        onBack={onBack}
        onNext={onContinue}
        nextDisabled={state !== 'found' && state !== 'not-found'}
        nextLabel={state === 'found' ? 'Continuar con estos datos' : 'Ingresar manualmente'}
      />
    </div>
  )
}

function RuesPreviewCard({ org }: { org: OrgState }) {
  return (
    <div className="rounded-lg bg-gradient-to-br from-success-tint to-bg-app p-4 ring-1 ring-success/30">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-5 w-5 text-success" />
          <span className="text-xs font-semibold uppercase tracking-wider text-success-fg">
            Empresa encontrada
          </span>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-wider text-fg-muted">
          NIT {org.nit}
        </span>
      </div>
      <div className="mt-3">
        <h3 className="text-base font-semibold text-fg-primary">{org.razon_social}</h3>
        {org.organizacion_juridica && (
          <p className="text-xs text-fg-muted">{org.organizacion_juridica}</p>
        )}
      </div>
      <dl className="mt-3 grid gap-1.5 text-sm sm:grid-cols-2">
        {org.email_corporativo && (
          <KV label="Correo">{org.email_corporativo}</KV>
        )}
        {org.rep_legal && <KV label="Rep. legal">{org.rep_legal}</KV>}
        {org.direccion && <KV label="Dirección">{org.direccion}</KV>}
        {(org.departamento || org.municipio) && (
          <KV label="Ubicación">
            {[org.municipio, org.departamento].filter(Boolean).join(', ')}
          </KV>
        )}
        {org.tamano_empresa && <KV label="Tamaño">{org.tamano_empresa}</KV>}
      </dl>
      {org.actividades_economicas.length > 0 && (
        <div className="mt-3 space-y-1.5 border-t border-success/20 pt-3">
          <div className="text-xs font-medium uppercase tracking-wider text-fg-muted">
            Actividades económicas
          </div>
          <ul className="space-y-0.5">
            {org.actividades_economicas.slice(0, 3).map((a) => (
              <li key={a} className="text-xs text-fg-secondary">
                · {a}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function KV({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[10px] font-medium uppercase tracking-wider text-fg-muted">{label}</dt>
      <dd className="text-sm text-fg-primary">{children}</dd>
    </div>
  )
}

// ─── Step: confirm org ──────────────────────────────────────────────

function ConfirmOrgStep({
  org,
  setOrg,
  onBack,
  onContinue,
}: {
  org: OrgState
  setOrg: React.Dispatch<React.SetStateAction<OrgState>>
  onBack: () => void
  onContinue: () => void
}) {
  const upd = (k: keyof OrgState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setOrg((prev) => ({ ...prev, [k]: e.target.value }))

  const valid =
    org.razon_social.trim() &&
    org.email_corporativo.trim() &&
    org.direccion.trim() &&
    org.departamento.trim() &&
    org.municipio.trim()

  return (
    <div className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold text-fg-primary">
          {org.found ? 'Confirma los datos de la empresa' : 'Ingresa los datos de la empresa'}
        </h2>
        <p className="mt-1 text-sm text-fg-secondary">
          {org.found
            ? 'Datos oficiales de tu empresa. Puedes ajustarlos si necesitas.'
            : 'Llénalos manualmente. Los validaremos antes de empezar a emitir.'}
        </p>
      </header>

      <div className="rounded-xl border border-border bg-bg-elevated p-5 shadow-sm">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="NIT" required>
            <input
              required
              value={org.nit}
              onChange={upd('nit')}
              readOnly={org.found === true}
              className={cn(inputStyles, 'font-mono', org.found === true && 'opacity-60')}
            />
          </Field>
          <Field label="Razón social" required>
            <input required value={org.razon_social} onChange={upd('razon_social')} className={inputStyles} />
          </Field>
        </div>

        <Field label="Correo corporativo" required hint="Recibirá un código distinto al tuyo personal.">
          <input
            required
            type="email"
            value={org.email_corporativo}
            onChange={upd('email_corporativo')}
            className={inputStyles}
          />
        </Field>

        <Field label="Dirección fiscal" required>
          <input required value={org.direccion} onChange={upd('direccion')} className={inputStyles} />
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Departamento" required>
            <input required value={org.departamento} onChange={upd('departamento')} className={inputStyles} />
          </Field>
          <Field label="Municipio" required>
            <input required value={org.municipio} onChange={upd('municipio')} className={inputStyles} />
          </Field>
        </div>

        {org.found === true && (
          <p className="mt-3 flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-wider text-fg-muted">
            <Shield className="h-3 w-3" />
            datos oficiales validados
          </p>
        )}
      </div>

      <NavRow onBack={onBack} onNext={onContinue} nextDisabled={!valid} />
    </div>
  )
}

// ─── Step: person form ─────────────────────────────────────────────

function PersonStep({
  accountType,
  person,
  setPerson,
  org,
  onBack,
  onSubmit,
  loading,
}: {
  accountType: AccountType
  person: PersonState
  setPerson: React.Dispatch<React.SetStateAction<PersonState>>
  org: OrgState
  onBack: () => void
  onSubmit: () => void
  loading: boolean
}) {
  const upd = (k: keyof PersonState) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setPerson((prev) => ({ ...prev, [k]: e.target.value }))

  const sameAsCorporate =
    accountType === 'empresa' &&
    person.email.trim().length > 0 &&
    person.email.trim().toLowerCase() === org.email_corporativo.trim().toLowerCase()

  // Consentimiento legal ya capturado en ChooseMethod step.
  const valid =
    person.first_name.trim() &&
    person.last_name.trim() &&
    person.email.trim() &&
    person.password.length >= 12 &&
    !sameAsCorporate

  function handle(e: React.FormEvent) {
    e.preventDefault()
    if (valid && !loading) onSubmit()
  }

  return (
    <form onSubmit={handle} className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold text-fg-primary">Tus datos personales</h2>
        <p className="mt-1 text-sm text-fg-secondary">
          {accountType === 'empresa'
            ? `Serás el dueño (OWNER) de ${org.razon_social || 'la empresa'}.`
            : 'Crearemos tu cuenta personal.'}
        </p>
      </header>

      <div className="rounded-xl border border-border bg-bg-elevated p-5 shadow-sm">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Nombre" required>
            <input required value={person.first_name} onChange={upd('first_name')} className={inputStyles} />
          </Field>
          <Field label="Apellido" required>
            <input required value={person.last_name} onChange={upd('last_name')} className={inputStyles} />
          </Field>
        </div>

        <Field
          label="Correo personal"
          required
          hint={
            accountType === 'empresa'
              ? 'Distinto al corporativo — recibirás un código aparte.'
              : 'Recibirás un código de verificación.'
          }
        >
          <input
            required
            type="email"
            value={person.email}
            onChange={upd('email')}
            className={cn(
              inputStyles,
              sameAsCorporate && 'border-error ring-1 ring-error',
            )}
          />
          {sameAsCorporate && (
            <span className="mt-1 block text-xs text-error">
              No puede ser igual al correo corporativo de la empresa.
            </span>
          )}
        </Field>

        <Field label="Contraseña" required>
          <PasswordInput value={person.password} onChange={upd('password')} />
        </Field>
      </div>

      <NavRow
        onBack={onBack}
        onNext={() => valid && onSubmit()}
        nextDisabled={!valid || loading}
        nextLabel={loading ? 'Enviando…' : 'Enviar código de verificación'}
        nextLoading={loading}
        submitButton
      />
    </form>
  )
}

function PasswordInput({
  value,
  onChange,
}: {
  value: string
  onChange: React.ChangeEventHandler<HTMLInputElement>
}) {
  const checks = [
    { label: '12 caracteres', ok: value.length >= 12 },
    { label: 'Mayúscula', ok: /[A-Z]/.test(value) },
    { label: 'Minúscula', ok: /[a-z]/.test(value) },
    { label: 'Número', ok: /\d/.test(value) },
    { label: 'Especial', ok: /[!@#$%^&*()_+\-=[\]{}|;:',.<>?/`~]/.test(value) },
  ]
  return (
    <div className="space-y-2">
      <input
        required
        type="password"
        minLength={12}
        value={value}
        onChange={onChange}
        className={inputStyles}
      />
      <div className="flex flex-wrap gap-1.5 text-[11px]">
        {checks.map((c) => (
          <span
            key={c.label}
            className={cn(
              'flex items-center gap-1 rounded-full px-2 py-0.5 font-mono uppercase tracking-wider transition-colors',
              c.ok
                ? 'bg-success-tint text-success-fg'
                : 'bg-bg-app text-fg-muted',
            )}
          >
            {c.ok ? <Check className="h-2.5 w-2.5" /> : <span className="h-2.5 w-2.5" />}
            {c.label}
          </span>
        ))}
      </div>
    </div>
  )
}

// ─── Step: verify OTP ───────────────────────────────────────────────

function VerifyOTPStep({
  accountType,
  start,
  personalCode,
  corporateCode,
  setPersonalCode,
  setCorporateCode,
  onSubmit,
  onResend,
  loading,
}: {
  accountType: AccountType
  start: RegisterStartResponse
  personalCode: string
  corporateCode: string
  setPersonalCode: (v: string) => void
  setCorporateCode: (v: string) => void
  onSubmit: (e: React.FormEvent) => void
  onResend: (target: 'personal' | 'corporate') => void
  loading: boolean
}) {
  const [secondsLeft, setSecondsLeft] = useState(start.expires_in)
  useEffect(() => {
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000)
    return () => clearInterval(id)
  }, [])
  const mm = Math.floor(secondsLeft / 60).toString().padStart(2, '0')
  const ss = (secondsLeft % 60).toString().padStart(2, '0')

  const valid =
    personalCode.length === 6 && (accountType === 'persona' || corporateCode.length === 6)

  return (
    <form onSubmit={onSubmit} className="space-y-5">
      <header>
        <h2 className="text-xl font-semibold text-fg-primary">Verifica tu correo</h2>
        <p className="mt-1 text-sm text-fg-secondary">
          Te enviamos un código a <code className="font-mono text-xs">{start.email_masked}</code>
          {accountType === 'empresa' && start.corporate_email_masked && (
            <>
              . Y otro a <code className="font-mono text-xs">{start.corporate_email_masked}</code>
            </>
          )}
          .
        </p>
        <p className="mt-2 inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-fg-muted">
          <Loader2 className={cn('h-3 w-3', secondsLeft === 0 ? '' : 'animate-spin')} />
          expira en {mm}:{ss}
        </p>
      </header>

      <div className="rounded-xl border border-border bg-bg-elevated p-5 shadow-sm">
        <OtpInput label="Código personal" value={personalCode} onChange={setPersonalCode} autoFocus />
        <ResendLink onClick={() => onResend('personal')} disabled={loading} target="personal" />

        {accountType === 'empresa' && (
          <>
            <div className="my-4 h-px bg-border" />
            <OtpInput label="Código corporativo" value={corporateCode} onChange={setCorporateCode} />
            <ResendLink onClick={() => onResend('corporate')} disabled={loading} target="corporate" />
          </>
        )}
      </div>

      <PrimaryButton type="submit" disabled={!valid || loading} className="w-full justify-center">
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Verificando
          </>
        ) : (
          <>
            <Shield className="h-4 w-4" />
            Confirmar
          </>
        )}
      </PrimaryButton>
    </form>
  )
}

function OtpInput({
  label,
  value,
  onChange,
  autoFocus,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  autoFocus?: boolean
}) {
  return (
    <Field label={label} required>
      <input
        required
        autoFocus={autoFocus}
        inputMode="numeric"
        pattern="\d{6}"
        maxLength={6}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, ''))}
        className={cn(
          inputStyles,
          'text-center font-mono text-2xl tracking-[0.5em]',
        )}
        placeholder="••••••"
      />
    </Field>
  )
}

function ResendLink({
  onClick,
  disabled,
  target,
}: {
  onClick: () => void
  disabled: boolean
  target: 'personal' | 'corporate'
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="mt-2 inline-flex items-center gap-1 text-xs text-fg-muted transition-colors hover:text-fg-primary disabled:opacity-40"
    >
      <RefreshCcw className="h-3 w-3" />
      Reenviar código {target === 'personal' ? 'personal' : 'corporativo'}
    </button>
  )
}

// ─── Step: done ─────────────────────────────────────────────────────

function DoneStep() {
  return (
    <div className="rounded-xl bg-gradient-to-br from-success-tint to-bg-app p-8 text-center ring-1 ring-success/30">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-success-tint">
        <CheckCircle2 className="h-8 w-8 text-success animate-in zoom-in duration-500" />
      </div>
      <h2 className="mt-4 text-xl font-semibold text-fg-primary">Cuenta creada</h2>
      <p className="mt-2 text-sm text-fg-secondary">Te llevamos al panel…</p>
    </div>
  )
}

// ─── Right value panel ─────────────────────────────────────────────

function ValuePanel() {
  return (
    <div className="flex h-full flex-col justify-between">
      <div>
        <div className="inline-flex items-center gap-2 font-mono text-xs">
          <span className="text-accent-500">▸</span>
          <span className="text-fg-muted">proveedor tecnológico autorizado DIAN</span>
        </div>

        <h2 className="mt-6 text-2xl font-bold text-fg-primary">
          Lo que pasa al crear tu cuenta
        </h2>

        <ol className="mt-6 space-y-5">
          <ValueStep
            n="01"
            icon={Search}
            title="Validamos los datos de tu empresa"
            body="Buscamos tu NIT en el registro mercantil oficial. Si existe, autocompletamos los datos para que no tengas que escribirlos."
          />
          <ValueStep
            n="02"
            icon={Mail}
            title="Verificamos tus correos"
            body="Te enviamos un código corto a tu correo personal — y otro al corporativo si registras una empresa. Confirmas con esos códigos."
          />
          <ValueStep
            n="03"
            icon={Shield}
            title="Tu cuenta queda lista"
            body="Acceso seguro al panel para empezar a emitir facturas electrónicas. Tu equipo puede sumarse después con sus propios accesos."
          />
        </ol>
      </div>

      <div className="mt-10 rounded-xl border border-border bg-gradient-to-br from-bg-elevated to-bg-app p-5">
        <div className="text-xs font-mono uppercase tracking-wider text-fg-muted">
          Una vez dentro
        </div>
        <p className="mt-2 text-sm text-fg-secondary">
          Emites tu primera factura en minutos. La DIAN responde con el acuse
          oficial y tu cliente la recibe en formato legalmente válido.
        </p>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-1 font-mono text-[10px] uppercase tracking-wider text-fg-muted">
        <span>Resol. DIAN 165/2023</span>
        <span aria-hidden className="text-fg-muted/40">·</span>
        <span>Decreto 358/2020</span>
        <span aria-hidden className="text-fg-muted/40">·</span>
        <span>6 años de respaldo legal</span>
      </div>
    </div>
  )
}

function ValueStep({
  n,
  icon: Icon,
  title,
  body,
}: {
  n: string
  icon: typeof User
  title: string
  body: string
}) {
  return (
    <li className="flex gap-4">
      <div className="flex flex-col items-center gap-2">
        <span className="font-mono text-[10px] text-fg-muted">{n}</span>
        <div className="flex h-10 w-10 items-center justify-center rounded-lg border border-border bg-bg-app text-fg-secondary">
          <Icon className="h-5 w-5" />
        </div>
      </div>
      <div className="pt-0.5">
        <h3 className="text-sm font-semibold text-fg-primary">{title}</h3>
        <p className="mt-1 text-sm text-fg-secondary">{body}</p>
      </div>
    </li>
  )
}

// ─── Shared UI helpers ─────────────────────────────────────────────

function PrimaryButton({
  type = 'button',
  children,
  className,
  disabled,
  onClick,
}: {
  type?: 'button' | 'submit'
  children: React.ReactNode
  className?: string
  disabled?: boolean
  onClick?: () => void
}) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        'inline-flex items-center gap-2 rounded-md bg-fg-primary px-4 py-2.5 text-sm font-medium text-bg-app transition-all hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40',
        className,
      )}
    >
      {children}
    </button>
  )
}

function SecondaryButton({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-2 rounded-md border border-border bg-bg-elevated px-4 py-2.5 text-sm font-medium text-fg-primary transition-colors hover:bg-bg-app disabled:opacity-40"
    >
      {children}
    </button>
  )
}

function NavRow({
  onBack,
  onNext,
  nextDisabled,
  nextLabel = 'Continuar',
  nextLoading,
  submitButton,
}: {
  onBack: () => void
  onNext: () => void
  nextDisabled?: boolean
  nextLabel?: string
  nextLoading?: boolean
  submitButton?: boolean
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <SecondaryButton onClick={onBack}>
        <ArrowLeft className="h-4 w-4" />
        Atrás
      </SecondaryButton>
      <PrimaryButton
        type={submitButton ? 'submit' : 'button'}
        onClick={submitButton ? undefined : onNext}
        disabled={nextDisabled}
      >
        {nextLoading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            {nextLabel}
          </>
        ) : (
          <>
            {nextLabel}
            <ArrowRight className="h-4 w-4" />
          </>
        )}
      </PrimaryButton>
    </div>
  )
}

function Field({
  label,
  required,
  hint,
  children,
}: {
  label: string
  required?: boolean
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-[11px] font-medium uppercase tracking-wider text-fg-muted">
        {label}
        {required && <span className="ml-0.5 text-error">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-fg-muted">{hint}</span>}
    </label>
  )
}

const inputStyles =
  'w-full rounded-md border border-border bg-bg-app px-3 py-2.5 text-sm text-fg-primary placeholder:text-fg-muted/60 outline-none transition-colors focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20'

function extractError(err: unknown): string {
  // Always log for debugging; UI string is friendly.
  // eslint-disable-next-line no-console
  console.error('[register] error:', err)

  const e = err as {
    code?: string
    message?: string
    response?: {
      status?: number
      data?: {
        detail?: { message?: string } | string | Array<{ msg?: string; loc?: string[] }>
        message?: string
      }
    }
  }

  // Network / no response
  if (e.code === 'ECONNABORTED' || /timeout/i.test(e.message ?? '')) {
    return 'El servidor tardó demasiado. Revisa tu conexión e inténtalo de nuevo.'
  }
  if (!e.response) {
    return 'No pudimos contactar al servidor. Revisa tu conexión.'
  }

  const status = e.response.status
  const detail = e.response.data?.detail

  // Pydantic 422 validation_errors come as an array
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    return first?.msg ?? 'Hay un campo con formato inválido.'
  }

  if (detail && !Array.isArray(detail)) {
    if (typeof detail === 'string') return detail
    if (typeof detail === 'object' && detail.message) return detail.message
  }

  if (e.response.data?.message) return e.response.data.message

  if (status && status >= 500) {
    return 'El servicio está temporalmente caído. Inténtalo en unos segundos.'
  }
  if (status === 429) return 'Demasiadas solicitudes. Espera un momento.'
  if (status === 409) return 'Ya existe una cuenta con esos datos.'

  return 'No pudimos completar la operación. Inténtalo de nuevo.'
}
